import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { MissingTimesheetShift } from '../api/types';
import {
  formatClock,
  formatDuration,
  formatShiftDateTime,
} from '../util/dateFormat';

import Avatar from './Avatar';

// Missing-timesheet card. Mirrors the No-show card (shift header + expand +
// pink worker rows), but each worker row shows the timesheet columns
// Status / Check-in / Check-out / Break / Total. Because "missing" means no
// data was ever captured, the time columns render "--:--" / "--" in red.

type Props = { shift: MissingTimesheetShift };

const MissingTimesheetShiftCard = ({ shift }: Props) => {
  const [open, setOpen] = useState(true);
  const tz = shift.timezone;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nscol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  const timeCol = (label: string, value: string, hasData: boolean) => (
    <div className="dashv2-tscol">
      <p className="dashv2-nslabel">{label}</p>
      <p className={`dashv2-tscol-val${hasData ? ' has-data' : ''}`}>{value}</p>
    </div>
  );

  return (
    <div className="dashv2-nscard">
      <div className="dashv2-nshead">
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Client', shift.client_name || '', 'client')}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        <button
          type="button"
          className={`dashv2-nsexpand${open ? ' is-open' : ''}`}
          aria-label={open ? 'Collapse workers' : 'Expand workers'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronRightIcon />
        </button>
      </div>

      {open && (
        <div className="dashv2-nsworkers">
          {shift.workers.map((w) => (
            <div className="dashv2-nsrow" key={w.id}>
              <div className="dashv2-nsuser">
                <Avatar name={w.name} src={w.avatar_url} />
                <span className="dashv2-nsname">
                  {w.name || 'Unknown worker'}
                </span>
              </div>
              <div className="dashv2-nsfield dashv2-nsfield--status-wide">
                <p className="dashv2-nslabel">Status</p>
                <span className="dashv2-nsbadge">Missing timesheet</span>
              </div>
              {timeCol('Check-in', formatClock(w.check_in, tz), !!w.check_in)}
              {timeCol(
                'Check-out',
                formatClock(w.check_out, tz),
                !!w.check_out,
              )}
              {timeCol(
                'Break',
                formatDuration(w.break_minutes),
                w.break_minutes != null,
              )}
              {timeCol(
                'Total',
                formatDuration(w.total_minutes),
                w.total_minutes != null,
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MissingTimesheetShiftCard;
